import { useForm } from "@inertiajs/react"
import { useCallback, useEffect, useRef, useState } from "react"

interface DuplicateVendor {
  id: number
  display_name: string
  company_name: string | null
  office_phone: string | null
  email: string | null
}

interface VendorForm {
  id?: number
  company_name: string
  first_name: string
  last_name: string
  vendor_type_id: number | null
  country_id: number | null
  address: string
  address2: string
  city: string
  state: string
  zip: string
  office_phone: string
  cell_phone: string
  fax: string
  email: string
  website: string
  notes: string
  hourly_rate: number | null
  travel_rate: number | null
  mileage_rate: number | null
  schedule: boolean
}

interface Option {
  id: number
  name: string
}

interface Props {
  vendor: VendorForm
  vendor_types: Option[]
  countries: Option[]
  errors?: Record<string, string[]>
  submitUrl: string
  submitMethod: "post" | "patch"
  group?: number | null
}

export default function VendorFormComponent({
  vendor,
  vendor_types,
  countries,
  errors,
  submitUrl,
  submitMethod,
  group,
}: Props) {
  const showRates = group === 1
  const { data, setData, post, patch, processing, transform } = useForm({
    company_name: vendor.company_name ?? "",
    first_name: vendor.first_name ?? "",
    last_name: vendor.last_name ?? "",
    vendor_type_id: vendor.vendor_type_id,
    country_id: vendor.country_id,
    address: vendor.address ?? "",
    address2: vendor.address2 ?? "",
    city: vendor.city ?? "",
    state: vendor.state ?? "",
    zip: vendor.zip ?? "",
    office_phone: vendor.office_phone ?? "",
    cell_phone: vendor.cell_phone ?? "",
    fax: vendor.fax ?? "",
    email: vendor.email ?? "",
    website: vendor.website ?? "",
    notes: vendor.notes ?? "",
    hourly_rate: vendor.hourly_rate,
    travel_rate: vendor.travel_rate,
    mileage_rate: vendor.mileage_rate,
    schedule: vendor.schedule || false,
  })

  transform((data) => ({ vendor: data }))

  const [duplicates, setDuplicates] = useState<DuplicateVendor[]>([])
  const [duplicateDismissed, setDuplicateDismissed] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const checkDuplicates = useCallback(() => {
    const params = new URLSearchParams()
    if (data.company_name) params.set("company_name", data.company_name)
    if (data.office_phone) params.set("phone", data.office_phone)
    if (data.cell_phone) params.set("cell_phone", data.cell_phone)
    if (data.email) params.set("email", data.email)
    if (vendor.id) params.set("exclude_id", String(vendor.id))
    if (
      !data.company_name &&
      !data.office_phone &&
      !data.cell_phone &&
      !data.email
    )
      return

    fetch(`/company/vendors/check_duplicates?${params.toString()}`, {
      headers: { Accept: "application/json" },
    })
      .then((r) => r.json())
      .then((json: { duplicates?: DuplicateVendor[] }) => {
        if (json.duplicates?.length) {
          setDuplicates(json.duplicates)
          setDuplicateDismissed(false)
        } else {
          setDuplicates([])
        }
      })
      .catch(() => {
        /* ignore */
      })
  }, [
    data.company_name,
    data.office_phone,
    data.cell_phone,
    data.email,
    vendor.id,
  ])

  const debouncedCheck = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(checkDuplicates, 500)
  }, [checkDuplicates])

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (submitMethod === "post") {
      post(submitUrl)
    } else {
      patch(submitUrl)
    }
  }

  const inputClass =
    "mt-1 block w-full rounded border border-hpi-light-gray px-3 py-2 focus:border-hpi-active-blue focus:outline-none"
  const labelClass = "block text-sm font-medium text-hpi-dark-gray"

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {duplicates.length > 0 && !duplicateDismissed && (
        <div className="rounded border border-yellow-400 bg-yellow-50 p-4">
          <div className="mb-2 flex items-center justify-between">
            <span className="font-medium text-yellow-800">
              Possible duplicate subcontractors found:
            </span>
            <button
              type="button"
              onClick={() => setDuplicateDismissed(true)}
              className="text-sm text-yellow-600 hover:text-yellow-800"
            >
              Dismiss
            </button>
          </div>
          <ul className="space-y-1">
            {duplicates.map((dup) => (
              <li key={dup.id} className="text-sm text-yellow-800">
                <a
                  href={`/company/vendors/${dup.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-yellow-900"
                >
                  {dup.display_name}
                </a>
                {dup.office_phone && ` - ${dup.office_phone}`}
                {dup.email && ` - ${dup.email}`}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Basic Information</h3>

          <div>
            <label className={labelClass}>Company Name</label>
            <input
              type="text"
              value={data.company_name}
              onChange={(e) => setData("company_name", e.target.value)}
              onBlur={debouncedCheck}
              className={inputClass}
            />
            {errors?.company_name && (
              <p className="text-hpi-red mt-1 text-sm">
                {errors.company_name[0]}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>First Name</label>
              <input
                type="text"
                value={data.first_name}
                onChange={(e) => setData("first_name", e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Last Name</label>
              <input
                type="text"
                value={data.last_name}
                onChange={(e) => setData("last_name", e.target.value)}
                className={inputClass}
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>Subcontractor Type</label>
            <select
              value={data.vendor_type_id ?? ""}
              onChange={(e) =>
                setData(
                  "vendor_type_id",
                  e.target.value ? parseInt(e.target.value) : null,
                )
              }
              className={inputClass}
            >
              <option value="">Select type...</option>
              {vendor_types.map((vt) => (
                <option key={vt.id} value={vt.id}>
                  {vt.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="text-lg font-medium">Contact Information</h3>

          <div>
            <label className={labelClass}>Office Phone</label>
            <input
              type="text"
              value={data.office_phone}
              onChange={(e) => setData("office_phone", e.target.value)}
              onBlur={debouncedCheck}
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>Cell Phone</label>
            <input
              type="text"
              value={data.cell_phone}
              onChange={(e) => setData("cell_phone", e.target.value)}
              onBlur={debouncedCheck}
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>Fax</label>
            <input
              type="text"
              value={data.fax}
              onChange={(e) => setData("fax", e.target.value)}
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>Email</label>
            <input
              type="email"
              value={data.email}
              onChange={(e) => setData("email", e.target.value)}
              onBlur={debouncedCheck}
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>Website</label>
            <input
              type="url"
              value={data.website}
              onChange={(e) => setData("website", e.target.value)}
              className={inputClass}
            />
          </div>
        </div>
      </div>

      <div
        className={`grid grid-cols-1 gap-6 ${showRates ? "md:grid-cols-2" : ""}`}
      >
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Address</h3>

          <div>
            <label className={labelClass}>Address</label>
            <input
              type="text"
              value={data.address}
              onChange={(e) => setData("address", e.target.value)}
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>Address 2</label>
            <input
              type="text"
              value={data.address2}
              onChange={(e) => setData("address2", e.target.value)}
              className={inputClass}
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className={labelClass}>City</label>
              <input
                type="text"
                value={data.city}
                onChange={(e) => setData("city", e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>State</label>
              <input
                type="text"
                value={data.state}
                onChange={(e) => setData("state", e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Zip</label>
              <input
                type="text"
                value={data.zip}
                onChange={(e) => setData("zip", e.target.value)}
                className={inputClass}
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>Country</label>
            <select
              value={data.country_id ?? ""}
              onChange={(e) =>
                setData(
                  "country_id",
                  e.target.value ? parseInt(e.target.value) : null,
                )
              }
              className={inputClass}
            >
              <option value="">Select country...</option>
              {countries.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {showRates && (
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Settings</h3>

            <div>
              <label className={labelClass}>Hourly Rate</label>
              <input
                type="number"
                step="0.01"
                value={data.hourly_rate ?? ""}
                onChange={(e) =>
                  setData(
                    "hourly_rate",
                    e.target.value ? parseFloat(e.target.value) : null,
                  )
                }
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>Travel Rate</label>
              <input
                type="number"
                step="0.01"
                value={data.travel_rate ?? ""}
                onChange={(e) =>
                  setData(
                    "travel_rate",
                    e.target.value ? parseFloat(e.target.value) : null,
                  )
                }
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>Mileage Rate</label>
              <input
                type="number"
                step="0.01"
                value={data.mileage_rate ?? ""}
                onChange={(e) =>
                  setData(
                    "mileage_rate",
                    e.target.value ? parseFloat(e.target.value) : null,
                  )
                }
                className={inputClass}
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="schedule"
                checked={data.schedule}
                onChange={(e) => setData("schedule", e.target.checked)}
                className="border-hpi-light-gray h-4 w-4 rounded"
              />
              <label
                htmlFor="schedule"
                className="text-hpi-dark-gray ml-2 text-sm"
              >
                Show in Subcontractors on Add/Update Schedule
              </label>
            </div>
          </div>
        )}
      </div>

      <div>
        <label className={labelClass}>Notes</label>
        <textarea
          value={data.notes}
          onChange={(e) => setData("notes", e.target.value)}
          rows={4}
          className={inputClass}
        />
      </div>

      <div className="flex justify-end space-x-4">
        <a
          href="/company/vendors"
          className="border-hpi-light-gray hover:bg-hpi-pale-blue rounded border px-4 py-2"
        >
          Cancel
        </a>
        <button
          type="submit"
          disabled={processing}
          className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white disabled:opacity-50"
        >
          {processing ? "Saving..." : "Save Subcontractor"}
        </button>
      </div>
    </form>
  )
}
